import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import TitleSection from "src/components/TitleSection/TitleSection";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const DownloadAndWin2026 = () => {
  const { t, i18n } = useTranslation();

  const hero = t("downloadAndWin2026.hero", { returnObjects: true });
  const sections = t("downloadAndWin2026.content.sections", {
    returnObjects: true,
  });

  const heroBgImage = useResponsiveImageUrl(
    "promotions/download-and-win-2026/pg-app-image-2400x1115px.jpg",
  );
  const ctaBgImage = useResponsiveImageUrl(
    "promotions/download-and-win-2026/pg-app-image-parralax.jpg",
  );

  const renderSection = (section, index) => {
    if (!section?.type) {
      return null;
    }

    if (section.type === "WeeklyPrizes") {
      return (
        <WeeklyPrizesSection key={`section-${index}`} section={section.props} />
      );
    }

    if (section.type === "StepsToEnter") {
      return (
        <StepsToEnterSection key={`section-${index}`} section={section.props} />
      );
    }

    if (section.type === "WhyYoullLove") {
      return (
        <WhyYoullLoveSection key={`section-${index}`} section={section.props} />
      );
    }

    if (section.type === "DownloadCTA") {
      return (
        <DownloadCTASection
          key={`section-${index}`}
          section={section.props}
          bgImage={ctaBgImage}
        />
      );
    }

    return null;
  };

  return (
    <PageWrapper>
      {/* Hero Section */}
      <HeroSection bgImage={heroBgImage}>
        <container-div>
          <Row className="align-items-center">
            <Col lg={5} className="text-col">
              <HeroLogo>
                <ResponsiveImage
                  src={`promotions/download-and-win-2026/download-and-win-logo-${i18n.language}.png`}
                  alt={hero?.title}
                />
              </HeroLogo>
              <h1>{hero?.subtitle}</h1>
              <ReactMarkdown>{hero?.description}</ReactMarkdown>
              <ButtonGroup>
                <AppStoreLink
                  href={hero?.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ResponsiveImage
                    src="promotions/download-and-win-2026/app-store.png"
                    alt="Download on the App Store"
                  />
                </AppStoreLink>
                <AppStoreLink
                  href={hero?.googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ResponsiveImage
                    src="promotions/download-and-win-2026/google-play.png"
                    alt="Get it on Google Play"
                  />
                </AppStoreLink>
              </ButtonGroup>
            </Col>
            <Col lg={7}>
              <HeroImageWrapper>
                <ResponsiveImage
                  src="promotions/download-and-win-2026/pg-app-hero-phone.png"
                  alt="Playground Rewards App"
                />
              </HeroImageWrapper>
            </Col>
          </Row>
        </container-div>
      </HeroSection>

      {/* Content Sections */}
      <MainContent>
        {(Array.isArray(sections) ? sections : []).map(renderSection)}
      </MainContent>
    </PageWrapper>
  );
};

// Weekly Prizes Section
const WeeklyPrizesSection = ({ section }) => {
  if (!section) return null;

  return (
    <WeeklyPrizesWrapper>
      <container-div>
        <TitleSection
          as="h2"
          title={section.title}
          text=""
          dark={false}
          className="weekly-prizes-title"
        />
        <ReactMarkdown className="description">
          {section.description}
        </ReactMarkdown>

        <Row className="justify-content-center g-4 prize-row">
          {section.prizes?.map((prize, index) => (
            <Col lg={6} md={12} key={index} className="m-3 m-md-0">
              <PrizeCard>
                <GiftBoxIcon>
                  <ResponsiveImage
                    src="promotions/download-and-win-2026/gift-box.svg"
                    alt="Gift box"
                  />
                </GiftBoxIcon>
                <PrizeRight>
                  <PrizeTopRow>
                    <PrizeNumber>{prize.number}</PrizeNumber>
                    <PrizeTitle>{prize.title}</PrizeTitle>
                  </PrizeTopRow>
                  <PrizeSubtitle>
                    <ReactMarkdown>{prize.subtitle}</ReactMarkdown>
                  </PrizeSubtitle>
                </PrizeRight>
              </PrizeCard>
            </Col>
          ))}
        </Row>
      </container-div>
    </WeeklyPrizesWrapper>
  );
};

// Steps to Enter Section
const StepsToEnterSection = ({ section }) => {
  if (!section) return null;

  return (
    <StepsWrapper>
      <container-div>
        <TitleSection
          as="h2"
          title={section.title}
          text=""
          dark={true}
          className="steps-title"
        />
        <ReactMarkdown className="description">
          {section.description}
        </ReactMarkdown>

        <Row className="align-items-stretch justify-content-center step-row">
          {section.steps?.map((step, index) => (
            <Col
              md={4}
              sm={12}
              key={index}
              className="position-relative d-flex step-col"
            >
              <StepCard>
                <StepIcon>
                  <ResponsiveImage
                    src={step.icon}
                    alt={`Step ${step.number} Icon`}
                  />
                </StepIcon>
                <StepRight>
                  <StepNumber>{step.number}</StepNumber>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    {step.subtitle && (
                      <StepSubtitle>{step.subtitle}</StepSubtitle>
                    )}
                  </StepContent>
                </StepRight>
              </StepCard>

              {index < section.steps.length - 1 && (
                <ArrowSeparator>
                  <ResponsiveImage
                    src="promotions/download-and-win-2026/next-arrow.svg"
                    alt="Next"
                  />
                </ArrowSeparator>
              )}
            </Col>
          ))}
        </Row>

        {section.disclaimer && (
          <DisclaimerWrapper>
            <ResponsiveImage
              src="promotions/download-and-win-2026/notification-full.svg"
              alt="Notification Bell"
              className="bell-icon"
            />
            <ReactMarkdown className="disclaimer">
              {section.disclaimer}
            </ReactMarkdown>
          </DisclaimerWrapper>
        )}
      </container-div>
    </StepsWrapper>
  );
};

// Why You'll Love Section
const WhyYoullLoveSection = ({ section }) => {
  if (!section) return null;

  return (
    <WhyLoveWrapper>
      <container-div>
        <TitleSection
          as="h2"
          title={section.title}
          text=""
          dark={true}
          className="why-love-title"
        />

        <Row className="justify-content-center g-4 feature-row">
          {section.features?.map((feature, index) => (
            <Col lg={3} md={6} sm={12} key={index}>
              <FeatureCard>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            </Col>
          ))}
        </Row>
      </container-div>
    </WhyLoveWrapper>
  );
};

// Download CTA Section
const DownloadCTASection = ({ section, bgImage }) => {
  if (!section) return null;

  return (
    <CTAWrapper bgImage={bgImage}>
      <container-div>
        <CTAContent>
          <CTATitle>
            {section.title} <br />
            <span className="italic">{section.subtitle}</span>
          </CTATitle>
          <ButtonGroup className="cta-buttons">
            <AppStoreLink
              href={section.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ResponsiveImage
                src="promotions/download-and-win-2026/app-store.png"
                alt="Download on the App Store"
              />
            </AppStoreLink>
            <AppStoreLink
              href={section.googlePlayUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ResponsiveImage
                src="promotions/download-and-win-2026/google-play.png"
                alt="Get it on Google Play"
              />
            </AppStoreLink>
          </ButtonGroup>
        </CTAContent>
      </container-div>
    </CTAWrapper>
  );
};

// Styled Components
const PageWrapper = styled.div`
  width: 100%;
  background: var(--white);
  overflow-x: hidden;
`;

const HeroSection = styled.div`
  width: 100%;
  background: url(${(props) => props.bgImage}) center center / cover no-repeat;
  padding-top: 140px;
  padding-bottom: 40px;
  text-align: center;

  @media (min-width: 991px) {
    padding-top: 150px;
    padding-bottom: 40px;
    text-align: left;
  }

  .text-col {
    display: flex;
    flex-direction: column;
    justify-content: center;

    h1 {
      font-size: 26px;
      font-weight: 900;
      margin: 1.5rem 0 20px;
      color: var(--black);
      text-transform: uppercase;

      @media (min-width: 768px) {
        font-size: 32px;
      }

      @media (min-width: 991px) {
        font-size: 38px;
      }
    }

    p {
      font-size: 16px;
      line-height: 1.6;
      margin-bottom: 20px;
      color: var(--lightblack);

      @media (min-width: 768px) {
        font-size: 18px;
      }

      strong {
        color: var(--black);
      }
    }
  }
`;

const HeroLogo = styled.div`
  max-width: 400px;
  margin: 0 auto;

  @media (min-width: 991px) {
    margin: 0;
    max-width: 500px;
  }

  img {
    width: 100%;
    height: auto;
  }
`;

const HeroImageWrapper = styled.div`
  margin-top: 2rem;

  @media (min-width: 991px) {
    margin-top: 0;
  }

  img {
    width: 100%;
    max-width: 700px;
    height: auto;
    margin: 0 auto;
    display: block;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: 991px) {
    justify-content: flex-start;
  }

  a {
    padding: 0;
    border-radius: 8px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent !important;
    border: none !important;
    overflow: visible;

    &:hover {
      opacity: 0.8;
      background: transparent !important;
    }

    img,
    svg,
    picture img {
      height: 50px;
      width: auto;
      display: block;
      max-width: none;
    }

    picture {
      display: flex;
      height: 50px;
    }
  }

  &.cta-buttons {
    justify-content: center;
  }
`;

const AppStoreLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 8px;
  transition: opacity 0.3s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  img,
  picture img {
    height: 50px;
    width: auto;
    display: block;
  }

  picture {
    display: flex;
    height: 50px;
  }
`;

const MainContent = styled.div`
  width: 100%;
  .title-section {
    padding: 0;
  }
`;

const WeeklyPrizesWrapper = styled.div`
  background: var(--black);
  padding: 50px 0;
  p {
    margin: 0;
  }
  @media (min-width: 768px) {
    padding: 40px 0;
  }

  .weekly-prizes-title {
    h2 {
      color: var(--white);
    }
    span {
      background: var(--red);
    }
  }

  .description {
    text-align: center;
    font-size: 16px;
    max-width: 700px;
    margin: 0 auto 2rem;
    color: var(--white);

    @media (min-width: 768px) {
      margin-bottom: 2rem;
    }
  }

  .prize-row {
    max-width: 1100px;
    margin: 0 auto;
  }
`;

/* --- REBUILT PRIZE CARD STRUCTURE --- */
const PrizeCard = styled.div`
  background: var(--white);
  border: 2px solid #cd0000;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  height: 100%;
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    padding: 1rem 2rem;
    gap: 2rem;
    text-align: center;
  }
`;

const GiftBoxIcon = styled.div`
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  background: var(--black);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    width: 160px;
    height: 160px;
  }

  img {
    width: 100%;
    height: auto;
  }
`;

const PrizeRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PrizeTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const PrizeNumber = styled.div`
  font-size: 80px;
  font-weight: 900;
  background: linear-gradient(180deg, #de3737 0%, #cd0000 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  line-height: 1.15;

  @media (min-width: 768px) {
    font-size: 90px;
  }
`;

const PrizeTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--black);
  text-transform: uppercase;
  line-height: 1.15;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 1.6rem;
    text-align: left;
  }
`;

const PrizeSubtitle = styled.div`
  border-top: 2px solid #cd0000;
  padding-top: 1rem;
  text-align: center;
  width: 100%;

  p {
    font-size: 14px;
    font-weight: 700;
    color: var(--black);
    margin: 0;

    strong {
      font-size: 16px;
      font-weight: 900;
      color: #cd0000;
    }
  }
`;
/* ---------------------------------- */

const StepsWrapper = styled.div`
  background: #e9ecef;
  color: var(--black);
  padding: 40px 0;

  @media (min-width: 768px) {
    padding: 40px 0 0 0;
  }

  .steps-title {
    span {
      background: var(--red);
    }
  }

  .description {
    text-align: center;
    font-size: 16px;
    max-width: 700px;
    margin: 0 auto 2rem;
    color: var(--black);
  }

  .step-row {
    max-width: 1100px;
    margin: 0 auto;
    row-gap: 10px;
  }

  .step-col {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const StepCard = styled.div`
  background: linear-gradient(180deg, #DE3737 0%, #CD0000 100%);
  border: 2px solid #CD0000;
  border-radius: 12px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 200px;

  @media (min-width: 768px) {
    flex-direction: row;
    padding: 24px 20px;
    gap: 20px;
    min-height: 180px;
  }
`;

const StepIcon = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    width: 100px;
    height: 100px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const StepRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const StepNumber = styled.div`
  width: 45px;
  height: 45px;
  background: var(--white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 900;
  color: #c32026;
  line-height: 1.15;

  @media (min-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 28px;
  }
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StepTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: var(--white);
  margin: 0;
  line-height: 1.3;
  text-align: center;
  white-space: pre-line;
`;

const StepSubtitle = styled.p`
  font-size: 13px;
  color: var(--white);
  margin: 0;
  opacity: 0.9;
  text-align: center;
`;

const ArrowSeparator = styled.div`
  position: absolute;
  width: 55px;
  height: 55px;
  background: #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  /* Mobile: positioned at bottom, arrow points down */
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%) rotate(90deg);

  /* Desktop: positioned at right, arrow points right */
  @media (min-width: 768px) {
    bottom: auto;
    left: auto;
    right: 0;
    top: 50%;
    transform: translate(50%, -50%);
  }

  img {
    width: 26px;
    height: auto;
    filter: brightness(0) invert(1);
  }
`;

const DisclaimerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 2rem;

  .bell-icon {
    width: 20px;
    height: auto;
  }

  .disclaimer {
    font-size: 14px;
    font-weight: 700;
    color: var(--black);
    margin: 0;
    p {
      margin: 0;
    }
  }
`;

const WhyLoveWrapper = styled.div`
  background: #e9ecef;
  padding: 0 0 40px;

  @media (min-width: 768px) {
    padding: 40px 0 40px;
  }

  .why-love-title {
    span {
      background: var(--red);
    }
  }

  .feature-row {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const FeatureCard = styled.div`
  background: var(--black);
  border-radius: 12px;
  padding: 1.75rem 1.25rem;
  height: 100%;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
`;

const FeatureTitle = styled.h4`
  font-size: 20px;
  font-weight: 900;
  color: var(--white);
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
  border-bottom: 2px solid #cd0000;
`;

const FeatureDescription = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: var(--white);
  margin: 0;
  line-height: 1.5;
`;

const CTAWrapper = styled.div`
  background: url(${(props) => props.bgImage}) center center / cover no-repeat;
  padding: 40px 0;
  text-align: center;
  background-attachment: fixed;

  @media (min-width: 768px) {
    padding: 40px 0;
  }
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 42px;
  font-weight: 900;
  color: var(--white);
  text-transform: uppercase;
  line-height: 1.1;
  max-width: 600px;
  font-style: italic;

  @media (min-width: 768px) {
    font-size: 52px;
  }

  .italic {
    color: var(--black);
    font-style: italic;
    font-weight: 900;
  }
`;

export default DownloadAndWin2026;
